import { useSupabase } from '@packages/supabase'
import { useCallback } from 'react'
import useSWR from 'swr'
import type {
  AdminFeature,
  FeaturePermissionRow,
  IcpAssignablePermission,
  SystemRoleDetail,
  SystemRoleRow,
  TenantCustomFeatureRow,
  TenantFeatureOverrideRow,
  TenantEffectiveFeatureRow,
  TenantTypeFeatureMatrixRow
} from './types'

export function useAdminFeatures() {
  const { supabase } = useSupabase()

  return useSWR<AdminFeature[]>('admin-features', async () => {
    const { data, error } = await supabase.rpc('admin_list_features')
    if (error) throw error
    return (data || []) as AdminFeature[]
  })
}

export function useFeaturePermissions() {
  const { supabase } = useSupabase()

  return useSWR<FeaturePermissionRow[]>(
    'admin-feature-permissions',
    async () => {
      const { data, error } = await supabase.rpc('admin_list_feature_permissions')
      if (error) throw error
      return (data || []) as FeaturePermissionRow[]
    }
  )
}

export function useTenantTypeFeatureMatrix() {
  const { supabase } = useSupabase()

  return useSWR<TenantTypeFeatureMatrixRow[]>(
    'admin-tenant-type-feature-matrix',
    async () => {
      const { data, error } = await supabase.rpc(
        'admin_fetch_tenant_type_feature_matrix'
      )
      if (error) throw error
      return (data || []) as TenantTypeFeatureMatrixRow[]
    }
  )
}

export function useSystemRoles() {
  const { supabase } = useSupabase()

  return useSWR<SystemRoleRow[]>('admin-system-roles', async () => {
    const { data, error } = await supabase.rpc('admin_list_system_roles')
    if (error) throw error
    return (data || []) as SystemRoleRow[]
  })
}

export function useIcpAssignablePermissions(tenantType: string) {
  const { supabase } = useSupabase()

  return useSWR<IcpAssignablePermission[]>(
    ['admin-icp-permissions', tenantType],
    async () => {
      const { data, error } = await supabase.rpc(
        'admin_list_icp_assignable_permissions',
        { p_tenant_type: tenantType }
      )
      if (error) throw error
      return (data || []) as IcpAssignablePermission[]
    }
  )
}

export function useFeatureAdminActions() {
  const { supabase } = useSupabase()

  const upsertFeature = useCallback(
    async (params: {
      key: string
      name: string
      description?: string | null
      sort_order?: number
      disabled?: boolean
      feature_id?: string | null
    }) => {
      const { data, error } = await supabase.rpc('admin_upsert_feature', {
        p_key: params.key,
        p_name: params.name,
        p_description: params.description ?? null,
        p_sort_order: params.sort_order ?? 0,
        p_disabled: params.disabled ?? false,
        p_feature_id: params.feature_id ?? null
      })
      if (error) throw error
      return data
    },
    [supabase]
  )

  const deleteFeature = useCallback(
    async (featureId: string) => {
      const { data, error } = await supabase.rpc('admin_delete_feature', {
        p_feature_id: featureId
      })
      if (error) throw error
      return data
    },
    [supabase]
  )

  const createPermission = useCallback(
    async (params: {
      name: string
      feature_id: string
      description?: string | null
      disabled?: boolean
    }) => {
      const { data, error } = await supabase.rpc('admin_create_permission', {
        p_name: params.name,
        p_feature_id: params.feature_id,
        p_description: params.description ?? null,
        p_disabled: params.disabled ?? false
      })
      if (error) throw error
      return data
    },
    [supabase]
  )

  const upsertPermission = useCallback(
    async (params: {
      permission_id: string
      description?: string | null
      disabled?: boolean
    }) => {
      const { data, error } = await supabase.rpc('admin_upsert_permission', {
        p_permission_id: params.permission_id,
        p_description: params.description ?? null,
        p_disabled: params.disabled ?? false
      })
      if (error) throw error
      return data
    },
    [supabase]
  )

  const setTenantTypeFeatures = useCallback(
    async (tenantType: string, featureKeys: string[]) => {
      const { data, error } = await supabase.rpc(
        'admin_set_tenant_type_features',
        {
          p_tenant_type: tenantType,
          p_feature_keys: featureKeys
        }
      )
      if (error) throw error
      return data
    },
    [supabase]
  )

  const fetchTenantCustomFeatures = useCallback(
    async (tenantId: string) => {
      const { data, error } = await supabase.rpc(
        'admin_fetch_tenant_custom_features',
        { p_tenant_id: tenantId }
      )
      if (error) throw error
      return (data || []) as TenantCustomFeatureRow[]
    },
    [supabase]
  )

  const setTenantCustomFeatures = useCallback(
    async (tenantId: string, featureKeys: string[]) => {
      const { data, error } = await supabase.rpc(
        'admin_set_tenant_custom_features',
        {
          p_tenant_id: tenantId,
          p_feature_keys: featureKeys
        }
      )
      if (error) throw error
      return data
    },
    [supabase]
  )

  const fetchTenantEffectiveFeatures = useCallback(
    async (tenantId: string) => {
      const { data, error } = await supabase.rpc(
        'admin_fetch_tenant_effective_features',
        { p_tenant_id: tenantId }
      )
      if (error) throw error
      return (data || []) as TenantEffectiveFeatureRow[]
    },
    [supabase]
  )

  const fetchTenantFeatureOverrides = useCallback(
    async (tenantId: string) => {
      const { data, error } = await supabase.rpc(
        'admin_fetch_tenant_feature_override_matrix',
        { p_tenant_id: tenantId }
      )
      if (error) throw error
      return (data || []) as TenantFeatureOverrideRow[]
    },
    [supabase]
  )

  const setTenantFeatureOverride = useCallback(
    async (tenantId: string, featureKey: string, enabled: boolean) => {
      const { error } = await supabase.rpc('admin_set_tenant_feature_override', {
        p_tenant_id: tenantId,
        p_feature_key: featureKey,
        p_enabled: enabled
      })
      if (error) throw error
    },
    [supabase]
  )

  const clearTenantFeatureOverride = useCallback(
    async (tenantId: string, featureKey: string) => {
      const { error } = await supabase.rpc('admin_clear_tenant_feature_override', {
        p_tenant_id: tenantId,
        p_feature_key: featureKey
      })
      if (error) throw error
    },
    [supabase]
  )

  const fetchSystemRole = useCallback(
    async (roleId: string) => {
      const { data, error } = await supabase.rpc('admin_fetch_system_role', {
        p_role_id: roleId
      })
      if (error) throw error
      return data as SystemRoleDetail
    },
    [supabase]
  )

  const updateSystemRole = useCallback(
    async (params: {
      role_id: string
      description?: string | null
      permission_ids: string[]
    }) => {
      const { data, error } = await supabase.rpc('admin_update_system_role', {
        p_role_id: params.role_id,
        p_description: params.description ?? null,
        p_permission_ids: params.permission_ids
      })
      if (error) throw error
      return data
    },
    [supabase]
  )

  return {
    upsertFeature,
    deleteFeature,
    createPermission,
    upsertPermission,
    setTenantTypeFeatures,
    fetchTenantCustomFeatures,
    setTenantCustomFeatures,
    fetchTenantEffectiveFeatures,
    fetchTenantFeatureOverrides,
    setTenantFeatureOverride,
    clearTenantFeatureOverride,
    fetchSystemRole,
    updateSystemRole
  }
}
